import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';
import { ModalController, PopoverController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Order, OrdersService, Product } from '@ubie/core';
import { ListItem } from '../../../interfaces/list-item.interface';
import { GeneralPopoverComponent } from '../../general/general-popover/general-popover.component';
import { ConfirmModalComponent } from '../../shared/confirm-modal/confirm-modal.component';
import { ErrorModalComponent } from '../../shared/error-modal/error-modal.component';
import { SuccessModalComponent } from '../../shared/success-modal/success-modal.component';

@Component({
  selector: 'acl-purchases-list',
  standalone: false,
  templateUrl: './purchases-list.component.html',
  styleUrl: './purchases-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PurchasesListComponent {
  private readonly popoverController = inject(PopoverController);
  private readonly modalController = inject(ModalController);
  private readonly ordersService = inject(OrdersService);
  private readonly translate = inject(TranslateService);

  readonly products = input<Product[]>();

  readonly hasNoProducts = computed(() => this.products()?.length === 0);

  private popover?: HTMLIonPopoverElement;

  /**
   * Add a year to a date
   * @param date
   */
  addYear(date: string): Date {
    const d = new Date(date);
    d.setUTCFullYear(d.getUTCFullYear() + 1);

    return d;
  }

  /** popover menu for configuration status changes */
  async presentPopover(ev: Event, order: Order): Promise<void> {
    const dropdownMenu: ListItem[] = [
      {
        name: this.translate.instant('order_delete'),
        click: () => this.handleOrderDelete(order),
      },
    ];

    this.popover = await this.popoverController.create({
      component: GeneralPopoverComponent,
      cssClass: 'general-popover',
      componentProps: { items: dropdownMenu, order: order },
      event: ev,
    });

    await this.popover.present();
  }

  async handleOrderDelete(order: Order): Promise<void> {
    // close the pop over
    await this.popover?.dismiss();

    if (order.id === undefined) {
      return;
    }

    const orderId: number = order.id;

    const modal = await this.modalController.create({
      component: ConfirmModalComponent,
      componentProps: {
        title: 'order_deleteTitle',
        message: 'order_deleteMessage',
      },
      cssClass: 'general-modal',
    });

    modal.onDidDismiss().then((dataReturned) => {
      if (dataReturned !== null) {
        if (dataReturned.data === 'yes') {
          this.ordersService.cancelRecurringPayment(orderId).subscribe({
            next: (isSuccess) => {
              if (isSuccess) {
                this.openSuccessModal(
                  'order_deleteTitle',
                  'order_deleteSucceeded',
                );
              } else {
                this.openErrorModal('order_deleteTitle', 'order_deleteFailed');
              }
            },
            error: () => {
              this.openErrorModal('order_deleteTitle', 'order_deleteFailed');
            },
          });
        }
      }
    });
    await modal.present();
  }

  // model for show success message
  async openSuccessModal(title: string, message: string): Promise<void> {
    const modal = await this.modalController.create({
      component: SuccessModalComponent,
      componentProps: { title: title, message: message },
    });

    modal.onDidDismiss().then();
    return await modal.present();
  }

  // model for show error message
  async openErrorModal(title: string, message: string): Promise<void> {
    const modal = await this.modalController.create({
      component: ErrorModalComponent,
      componentProps: { title: title, message: message },
    });

    modal.onDidDismiss().then();

    return await modal.present();
  }
}
